import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';

import { pdfPreviewProvider } from '../../providers/pdfPreviewProvider';
import { propertyAdEditorProvider } from '../../providers/propertyAdEditorProvider';
import type {
  PdfConfirmAction,
  PdfConfirmFieldName,
  PdfConfirmFieldValue,
  PdfConfirmRequest,
  PdfPreviewEdits,
  PdfPreviewSnapshot,
} from '../../types/pdfPreview';
import { PDF_SHARED_FIELDS } from '../../types/pdfPreview';
import type { PropertyAdInProgressWorkspace } from '../../types/propertyAdEditor';
import { MyButton, MyDialog, SectionHeader } from '../uiComponents';
import { EditorSection } from './EditorSection';
import { pdfFilledButtonSx } from './pdfImportButtonStyles';
import {
  createPdfPreviewEdits,
  buildPdfConfirmRequest,
  effectiveConfirmValue,
  isPdfPreviewDirty,
  moveUnitMedia,
  originalConfirmValue,
  previewUnits,
  setProjectOverride,
  setPdfConfirmAction,
  setPdfPartialAcknowledgment,
  setUnitOverride,
  toggleUnitMedia,
} from './pdfPreviewState';
import { PdfPreviewFields } from './PdfPreviewFields';
import { PdfPreviewMedia } from './PdfPreviewMedia';
import { usePdfConfirmOperation } from './usePdfConfirmOperation';

type Phase = 'loading' | 'ready' | 'empty' | 'expired' | 'superseded' | 'forbidden' | 'failed';

const phaseForFailure = (status?: number | string, code?: string): Phase => {
  if (code === 'pdf_preview_expired') return 'expired';
  if (code === 'pdf_preview_superseded') return 'superseded';
  if (status === 403 || code === 'pdf_import_disabled' || status === 404) return 'forbidden';
  return 'failed';
};

export const PdfPreviewEntry = () => {
  const { previewId } = useParams<{ previewId: string }>();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('loading');
  const [snapshot, setSnapshot] = useState<PdfPreviewSnapshot | null>(null);
  const [edits, setEdits] = useState<PdfPreviewEdits | null>(null);
  const [selectedUnitKey, setSelectedUnitKey] = useState<string | null>(null);
  const [openProjects, setOpenProjects] = useState<Record<string, boolean>>({});
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [consentWorkspace, setConsentWorkspace] = useState<PropertyAdInProgressWorkspace | null>(
    null
  );
  const [consentRequest, setConsentRequest] = useState<PdfConfirmRequest | null>(null);
  const preflightLock = useRef(false);
  const confirm = usePdfConfirmOperation(previewId);
  const dirty =
    confirm.phase !== 'completed' &&
    (confirm.pending !== null ||
      confirm.phase === 'unknown' ||
      (snapshot !== null && edits !== null && isPdfPreviewDirty(snapshot, edits)));
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirtyRef.current && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    const controller = new AbortController();
    setPhase('loading');
    setSnapshot(null);
    setEdits(null);
    setOpenProjects({});
    setConfirmError(null);
    const load = async () => {
      if (!previewId) {
        setPhase('failed');
        return;
      }
      try {
        const response = await pdfPreviewProvider.getPreview(previewId, controller.signal);
        if (controller.signal.aborted) return;
        if (!response.ok || !response.data) {
          setPhase(phaseForFailure(response.status, response.code));
          return;
        }
        if (Date.parse(response.data.expiresAt) <= Date.now()) {
          setPhase('expired');
          return;
        }
        const units = previewUnits(response.data);
        setSnapshot(response.data);
        setEdits(createPdfPreviewEdits(response.data));
        setSelectedUnitKey(units[0]?.unit.unitKey ?? null);
        setPhase(units.length ? 'ready' : 'empty');
      } catch {
        if (!controller.signal.aborted) setPhase('failed');
      }
    };
    void load();
    return () => controller.abort();
  }, [previewId]);

  useEffect(() => {
    if (!snapshot || phase === 'expired' || phase === 'superseded') return;
    const remaining = Date.parse(snapshot.expiresAt) - Date.now();
    if (remaining <= 0) {
      setPhase('expired');
      return;
    }
    const timeout = setTimeout(() => setPhase('expired'), remaining);
    return () => clearTimeout(timeout);
  }, [snapshot, phase]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const expire = useCallback(() => setPhase('expired'), []);
  const updateEdits = (update: (current: PdfPreviewEdits) => PdfPreviewEdits) => {
    if (phase !== 'ready' || confirm.pending || confirm.phase === 'completed') return;
    setEdits((current) => (current ? update(current) : current));
  };
  const chooseAction = (unitKey: string, action: PdfConfirmAction | null) => {
    if (!edits || confirm.pending || confirm.phase === 'completed') return;
    if (
      action === 'editor' &&
      Object.entries(edits.actions).some(([key, chosen]) => key !== unitKey && chosen === 'editor')
    ) {
      setConfirmError('Only one listing can open in the Editor. Change the previous choice first.');
      return;
    }
    setConfirmError(null);
    updateEdits((current) => setPdfConfirmAction(current, unitKey, action));
  };
  const submitConfirm = async () => {
    if (
      !snapshot ||
      !edits ||
      !previewId ||
      preflightLock.current ||
      confirm.pending ||
      confirm.phase === 'unknown' ||
      confirm.phase === 'completed' ||
      (phase !== 'ready' && phase !== 'empty')
    )
      return;
    preflightLock.current = true;
    setConfirmError(null);
    try {
      const built = buildPdfConfirmRequest(snapshot, edits);
      if (!built.ok) {
        setConfirmError(built.error);
        if (built.unitKey) setSelectedUnitKey(built.unitKey);
        return;
      }
      if (built.request.decisions.some((decision) => decision.action === 'editor')) {
        const workspace = await propertyAdEditorProvider.getInProgressWorkspace();
        if (!workspace.ok) {
          setConfirmError(
            'Could not check the current unfinished listing. Try again before confirming.'
          );
          return;
        }
        if (workspace.data) {
          setConsentWorkspace(workspace.data);
          setConsentRequest(built.request);
          return;
        }
      }
      await confirm.start(built.request);
    } catch {
      setConfirmError(
        'Could not check the current unfinished listing. Try again before confirming.'
      );
    } finally {
      preflightLock.current = false;
    }
  };
  const acceptReplacement = async () => {
    if (!consentWorkspace || !consentRequest || preflightLock.current) return;
    preflightLock.current = true;
    setConsentWorkspace(null);
    setConsentRequest(null);
    try {
      await confirm.start({
        ...consentRequest,
        replaceExistingInProgress: {
          approved: true,
          existingPropertyAdId: consentWorkspace.id,
          expectedVersion: consentWorkspace.version,
        },
      });
    } finally {
      preflightLock.current = false;
    }
  };
  const goBack = () => navigate('/ads/create/pdf');
  const stay = () => {
    if (blocker.state === 'blocked') blocker.reset();
  };
  const leave = () => {
    if (blocker.state === 'blocked') blocker.proceed();
  };
  const button = (label: string, onClick: () => void, disabled = false) => (
    <MyButton
      data={{
        buttonName: label,
        buttonType: 'button',
        variant: 'contained',
        sx: pdfFilledButtonSx,
      }}
      disabled={disabled}
      onClick={onClick}
    />
  );
  const units = snapshot ? previewUnits(snapshot) : [];
  const selected = units.find(({ unit }) => unit.unitKey === selectedUnitKey) ?? units[0];
  const selectedEdits = selected ? edits?.units[selected.unit.unitKey] : undefined;
  const selectedProject = snapshot?.projects.find(
    (project) => project.projectKey === selected?.projectKey
  );
  const editorCount = units.filter(({ unit }) => edits?.actions[unit.unitKey] === 'editor').length;
  const draftCount = units.filter(({ unit }) => edits?.actions[unit.unitKey] === 'draft').length;
  const skipCount = units.filter(({ unit }) => edits?.actions[unit.unitKey] === 'skip').length;
  const allDecided = units.every(({ unit }) => Boolean(edits?.actions[unit.unitKey]));
  const partialRequired =
    snapshot?.processingStatus === 'partial' || snapshot?.requiresPartialAcknowledgment === true;
  const selectedPhotoCount = units.reduce(
    (total, { unit }) =>
      total +
      (edits?.actions[unit.unitKey] === 'skip'
        ? 0
        : edits?.units[unit.unitKey]?.selectedMediaIds.length ?? 0),
    0
  );
  const reviewCount = units.filter(
    ({ unit }) =>
      unit.review.length > 0 || Object.values(unit.fields).some((field) => field.needsReview)
  ).length;

  return (
    <Box component='section' data-testid='pdf-preview-entry' minWidth={0} width='100%'>
      <SectionHeader title='Review PDF listing preview' titleComponent='h1' />
      <Stack paddingY={3} spacing={2.5}>
        {button('Back to PDF import', goBack)}
        {phase === 'loading' && (
          <Stack alignItems='center' spacing={1}>
            <CircularProgress aria-label='Loading PDF preview' />
            <Typography>Loading preview...</Typography>
          </Stack>
        )}
        {phase === 'forbidden' && (
          <Typography role='alert'>This PDF preview is unavailable to your account.</Typography>
        )}
        {phase === 'superseded' && (
          <Typography role='alert'>
            This preview was replaced by a newer one. Return to PDF import.
          </Typography>
        )}
        {phase === 'expired' && (
          <Typography role='alert'>
            This preview has expired. Editing is unavailable. Return to PDF import.
          </Typography>
        )}
        {phase === 'failed' && (
          <Typography role='alert'>
            Unable to load the PDF preview. Please return and check the status.
          </Typography>
        )}
        {snapshot && (phase === 'ready' || phase === 'empty' || phase === 'expired') && (
          <>
            <EditorSection title='Preview status' titleId='pdf-preview-status'>
              <Stack spacing={1}>
                <Typography>
                  Available until {new Date(snapshot.expiresAt).toLocaleString()}
                </Typography>
                <Typography>Analysis: {snapshot.processingStatus}</Typography>
                {snapshot.processingStatus === 'partial' && (
                  <Typography color='warning.main' role='alert'>
                    Partial analysis. Some document content could not be verified. Review all fields
                    and sources before continuing in a later step.
                  </Typography>
                )}
                {snapshot.incomplete && (
                  <Typography color='warning.main'>Document coverage is incomplete.</Typography>
                )}
                {snapshot.incompleteBusinessData && (
                  <Typography color='warning.main'>Some business fields are incomplete.</Typography>
                )}
                {snapshot.coverage.entries
                  .filter((entry) =>
                    ['failed', 'deferred', 'skipped_with_reason'].includes(entry.state)
                  )
                  .map((entry) => (
                    <Typography key={entry.id} color='warning.main' variant='body2'>
                      Page {entry.pageNumbers.join(', ') || '?'}: {entry.reason ?? entry.state}
                    </Typography>
                  ))}
                {snapshot.unresolvedSectionIds.length > 0 && (
                  <Typography color='warning.main' variant='body2'>
                    {snapshot.unresolvedSectionIds.length} document sections need review.
                  </Typography>
                )}
                {snapshot.sourceObservations.map((observation, index) => (
                  <Typography key={index} color='warning.main' variant='body2'>
                    Source observation: {observation.kind}
                    {observation.sources.length
                      ? ` (page ${observation.sources.map((source) => source.pageNumber).join(', ')})`
                      : ''}
                  </Typography>
                ))}
                {snapshot.possibleDuplicates.length > 0 && (
                  <Typography color='warning.main' variant='body2'>
                    {snapshot.possibleDuplicates.length} possible duplicate listing pairs need
                    review.
                  </Typography>
                )}
                {snapshot.review.map((item, index) => (
                  <Typography key={`${item.code}-${index}`} color='warning.main' variant='body2'>
                    Review: {item.code} ({item.field})
                  </Typography>
                ))}
              </Stack>
            </EditorSection>
            {phase === 'empty' && (
              <EditorSection title='No listings found' titleId='pdf-preview-empty'>
                <Typography>
                  No property listings were identified in this PDF. You can return to PDF import and
                  try another document.
                </Typography>
              </EditorSection>
            )}
            {snapshot.projects.map((project, index) => (
              <EditorSection
                key={project.projectKey}
                title={
                  project.sharedProject.fields.title?.state === 'confirmed' &&
                  project.sharedProject.title
                    ? project.sharedProject.title
                    : `Project ${index + 1}`
                }
                titleId={`pdf-project-${index}`}
              >
                <Stack spacing={2}>
                  <Typography>{project.units.length} listing variants</Typography>
                  <Stack direction='row' flexWrap='wrap' gap={1}>
                    {project.units.map((unit, unitIndex) => (
                      <Box key={unit.unitKey}>
                        {button(
                          `Listing ${unitIndex + 1}: ${unit.title || unit.candidateRef || 'Untitled'}`,
                          () => setSelectedUnitKey(unit.unitKey),
                          phase !== 'ready'
                        )}
                      </Box>
                    ))}
                  </Stack>
                  {phase === 'ready' && edits && (
                    <>
                      {button(
                        openProjects[project.projectKey]
                          ? 'Hide shared project fields'
                          : 'Edit shared project fields',
                        () =>
                          setOpenProjects((current) => ({
                            ...current,
                            [project.projectKey]: !current[project.projectKey],
                          }))
                      )}
                      <Collapse in={Boolean(openProjects[project.projectKey])} unmountOnExit>
                        <PdfPreviewFields
                          source={project.sharedProject}
                          shared
                          disabled={Boolean(confirm.pending) || confirm.phase === 'completed'}
                          values={(field) => {
                            const override = edits.projectOverrides[project.projectKey];
                            return override && Object.prototype.hasOwnProperty.call(override, field)
                              ? override[field] ?? null
                              : originalConfirmValue(project.sharedProject, field);
                          }}
                          onChange={(field: PdfConfirmFieldName, value: PdfConfirmFieldValue) => {
                            if ((PDF_SHARED_FIELDS as readonly string[]).includes(field))
                              updateEdits((current) =>
                                setProjectOverride(current, project.projectKey, field, value)
                              );
                          }}
                        />
                      </Collapse>
                    </>
                  )}
                </Stack>
              </EditorSection>
            ))}
            {snapshot.ungroupedUnits.length > 0 && (
              <EditorSection title='Other listings' titleId='pdf-ungrouped'>
                <Stack direction='row' flexWrap='wrap' gap={1}>
                  {snapshot.ungroupedUnits.map((unit, index) => (
                    <Box key={unit.unitKey}>
                      {button(
                        `Listing ${index + 1}: ${unit.title || unit.candidateRef || 'Untitled'}`,
                        () => setSelectedUnitKey(unit.unitKey),
                        phase !== 'ready'
                      )}
                    </Box>
                  ))}
                </Stack>
              </EditorSection>
            )}
            {phase === 'ready' && selected && edits && (
              <>
                <EditorSection
                  title={selected.unit.title || 'Listing details'}
                  titleId='pdf-unit-details'
                >
                  <Stack spacing={2}>
                    <Typography variant='caption'>
                      Source reference: {selected.unit.candidateRef || 'unavailable'}
                    </Typography>
                    <PdfPreviewFields
                      key={selected.unit.unitKey}
                      source={selected.unit}
                      disabled={Boolean(confirm.pending) || confirm.phase === 'completed'}
                      values={(field) =>
                        effectiveConfirmValue(
                          selected.unit,
                          field,
                          edits,
                          selected.projectKey,
                          selectedProject
                            ? originalConfirmValue(selectedProject.sharedProject, 'title')
                            : undefined
                        )
                      }
                      onChange={(field, value) =>
                        updateEdits((current) =>
                          setUnitOverride(current, selected.unit.unitKey, field, value)
                        )
                      }
                    />
                  </Stack>
                </EditorSection>
                <EditorSection title='Photos and floorplans' titleId='pdf-unit-media'>
                  <PdfPreviewMedia
                    key={snapshot.previewId}
                    previewId={snapshot.previewId}
                    media={snapshot.media ?? []}
                    unit={selected.unit}
                    selectedMediaIds={selectedEdits?.selectedMediaIds ?? []}
                    disabled={Boolean(confirm.pending) || confirm.phase === 'completed'}
                    onExpired={expire}
                    onToggle={(mediaId) =>
                      updateEdits((current) =>
                        toggleUnitMedia(snapshot, current, selected.unit.unitKey, mediaId)
                      )
                    }
                    onMove={(mediaId, direction) =>
                      updateEdits((current) =>
                        moveUnitMedia(current, selected.unit.unitKey, mediaId, direction)
                      )
                    }
                  />
                </EditorSection>
                <Typography variant='body2'>
                  Your review changes stay on this page until confirmation.
                </Typography>
              </>
            )}
          </>
        )}
        {snapshot &&
          edits &&
          (phase === 'ready' || phase === 'empty') &&
          confirm.phase !== 'completed' && (
            <EditorSection title='Confirm imported listings' titleId='pdf-confirm-decisions'>
              <Stack spacing={2}>
                <Typography>
                  Choose what to do with each listing. Nothing will be published automatically.
                </Typography>
                {units.map(({ unit }) => (
                  <Box key={unit.unitKey}>
                    <Typography fontWeight={600}>
                      {unit.title || unit.candidateRef || unit.unitKey}
                    </Typography>
                    <RadioGroup
                      row
                      aria-label={`Action for ${unit.title || unit.unitKey}`}
                      value={edits.actions[unit.unitKey] ?? ''}
                      onChange={(event) =>
                        chooseAction(unit.unitKey, event.target.value as PdfConfirmAction)
                      }
                    >
                      <FormControlLabel
                        value='editor'
                        control={<Radio />}
                        label='Editor'
                        disabled={Boolean(confirm.pending)}
                      />
                      <FormControlLabel
                        value='draft'
                        control={<Radio />}
                        label='Draft'
                        disabled={Boolean(confirm.pending)}
                      />
                      <FormControlLabel
                        value='skip'
                        control={<Radio />}
                        label='Skip'
                        disabled={Boolean(confirm.pending)}
                      />
                    </RadioGroup>
                    {edits.actions[unit.unitKey] &&
                      button(
                        'Clear decision',
                        () => chooseAction(unit.unitKey, null),
                        Boolean(confirm.pending)
                      )}
                    {(unit.review.length > 0 ||
                      Object.values(unit.fields).some((field) => field.needsReview)) && (
                      <Typography color='warning.main' variant='body2'>
                        Review required
                        {unit.review.length > 0 ? `: ${unit.review.length} notes` : ''}
                      </Typography>
                    )}
                  </Box>
                ))}
                {!allDecided && (
                  <Typography color='warning.main'>
                    Choose Editor, Draft or Skip for every listing.
                  </Typography>
                )}
                <Typography>
                  Summary: {editorCount} Editor, {draftCount} Draft, {skipCount} Skip;{' '}
                  {selectedPhotoCount} selected photos; {reviewCount} listings need review.
                </Typography>
                {editorCount > 0 && (
                  <Typography variant='body2'>
                    The Editor selection will open in the existing PropertyAd Editor. Any current
                    unfinished listing will be checked first.
                  </Typography>
                )}
                {draftCount > 0 && (
                  <Typography variant='body2'>
                    Draft listings will be saved without publishing.
                  </Typography>
                )}
                {skipCount > 0 && (
                  <Typography variant='body2'>
                    Skipped listings and their photos will not be created.
                  </Typography>
                )}
                {partialRequired && (
                  <Stack>
                    <Typography color='warning.main'>
                      Some parts of this PDF could not be analyzed. The imported listings may be
                      incomplete.
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={edits.acknowledgePartial}
                          disabled={Boolean(confirm.pending)}
                          onChange={(event) =>
                            updateEdits((current) =>
                              setPdfPartialAcknowledgment(current, event.target.checked)
                            )
                          }
                        />
                      }
                      label='I understand and want to continue.'
                    />
                  </Stack>
                )}
                {confirmError && (
                  <Typography role='alert' color='error.main'>
                    {confirmError}
                  </Typography>
                )}
                {button(
                  'Confirm imported listings',
                  () => void submitConfirm(),
                  !allDecided ||
                    (partialRequired && !edits.acknowledgePartial) ||
                    Boolean(confirm.pending) ||
                    confirm.phase === 'unknown' ||
                    preflightLock.current
                )}
              </Stack>
            </EditorSection>
          )}
        {confirm.phase !== 'idle' && confirm.phase !== 'completed' && (
          <EditorSection title='Confirmation status' titleId='pdf-confirm-status'>
            <Stack spacing={2}>
              {(confirm.phase === 'submitting' || confirm.phase === 'processing') && (
                <CircularProgress aria-label='Confirming imported listings' />
              )}
              <Typography role='status'>
                {confirm.phase === 'submitting'
                  ? 'Submitting confirmation...'
                  : confirm.phase === 'processing'
                    ? 'Confirmation is processing. Your listings are not ready yet.'
                    : confirm.phase === 'unknown'
                      ? confirm.error
                        ? 'Unable to verify confirmation. The result is still unknown.'
                        : 'Checking confirmation. The result is not yet known.'
                      : 'Confirmation could not complete.'}
              </Typography>
              {confirm.error && (
                <Typography role='alert' color='error.main'>
                  {confirm.error}
                </Typography>
              )}
              {confirm.pending?.confirmId &&
                button('Check status', () => void confirm.checkStatus())}
              {confirm.pending &&
                !confirm.pending.confirmId &&
                button('Check confirmation', () => void confirm.lookup())}
              {confirm.pending &&
                confirm.replayAvailable &&
                button('Continue same confirmation', () => void confirm.replay())}
              {confirm.phase === 'unknown' && confirm.pending && (
                <Typography variant='body2'>
                  The result is unknown. Check this confirmation and your listings before trying
                  again. Your original request and Idempotency-Key are saved in this browser.
                </Typography>
              )}
            </Stack>
          </EditorSection>
        )}
        {confirm.result?.status === 'completed' && (
          <EditorSection title='Imported listings' titleId='pdf-confirm-result'>
            <Stack spacing={2}>
              <Typography role='status'>
                Created {confirm.result.units.filter((unit) => unit.propertyAdId).length} listings:{' '}
                {confirm.result.editorPropertyAdId ? 1 : 0} Editor,{' '}
                {confirm.result.createdDraftIds.length} Draft,{' '}
                {confirm.result.skippedUnitKeys.length} Skip.
              </Typography>
              {confirm.result.units.map((unit) => (
                <Typography key={unit.unitKey} variant='body2'>
                  {unit.action.toUpperCase()} · {unit.unitKey} ·{' '}
                  {unit.propertyAdId ? `PropertyAd ${unit.propertyAdId}` : 'No listing created'} ·{' '}
                  {unit.mediaCount} photos
                </Typography>
              ))}
              {confirm.result.editorPropertyAdId &&
                button('Open in Editor', () =>
                  navigate(`/ads/edit/${confirm.result?.editorPropertyAdId}`)
                )}
              {button('My Properties', () => navigate('/ads/my'))}
            </Stack>
          </EditorSection>
        )}
      </Stack>
      <MyDialog
        open={Boolean(consentWorkspace)}
        dialogTitle='Replace unfinished listing?'
        width='500'
        onDismiss={() => {
          setConsentWorkspace(null);
          setConsentRequest(null);
        }}
        onClose={() => {
          setConsentWorkspace(null);
          setConsentRequest(null);
        }}
      >
        <Stack spacing={2} alignItems='center'>
          <Typography>
            You already have an unfinished listing. To open the imported property in the Editor,
            your current unfinished listing will be saved as a Draft. Do you want to continue?
          </Typography>
          <Stack direction='row' gap={2}>
            {button('Cancel', () => {
              setConsentWorkspace(null);
              setConsentRequest(null);
            })}
            {button('Save existing as Draft and continue', () => void acceptReplacement())}
          </Stack>
        </Stack>
      </MyDialog>
      <MyDialog
        open={blocker.state === 'blocked'}
        dialogTitle='Leave PDF preview?'
        width='480'
        onDismiss={stay}
        onClose={stay}
      >
        <Stack alignItems='center' spacing={2}>
          <Typography>
            Your changes haven&apos;t been saved. If you leave this page, they will be lost.
          </Typography>
          <Stack direction='row' gap={2}>
            {button('Stay', stay)}
            {button('Leave without saving', leave)}
          </Stack>
        </Stack>
      </MyDialog>
    </Box>
  );
};
